import { useState, useEffect } from 'react'
import styles from './MiniCalendar.module.css'
import { formatMonthYear, isToday, moment } from '../utils/dateUtils'

export default function MiniCalendar({ 
    currentDate, 
    selectedDate, 
    onDateChange, 
    onMonthChange, 
    worklogs = [],
    selectedTeammates = [],
    currentUser = null
}) {
    const [viewDate, setViewDate] = useState(currentDate)
    
    // Sync viewDate with currentDate changes (e.g., from week navigation)
    useEffect(() => {
        setViewDate(currentDate)
    }, [currentDate])
    
    // Helper functions
    const getDaysInMonth = (date) => {
        return moment(date).daysInMonth()
    }

    const getFirstDayOfMonth = (date) => {
        const firstDay = moment(date).startOf('month').day()
        // Convert Sunday=0 to Monday=0 system (Sunday becomes 6, Monday becomes 0)
        return firstDay === 0 ? 6 : firstDay - 1
    }

    const getMonthName = (date) => {
        return formatMonthYear(date)
    }

    const isSameDay = (date1, date2) => {
        return moment(date1).isSame(moment(date2), 'day')
    }

    // Filter worklogs by selected teammates (same logic as in Calendar.jsx)
    const getVisibleWorklogs = () => {
        return worklogs.filter(worklog => {
            // Show worklog if user is selected or if it's current user's worklog and 'self' is selected
            const isCurrentUserWorklog = worklog.userId === currentUser?.id
            const isUserSelected = selectedTeammates.includes(worklog.userId)
            const isSelfSelected = selectedTeammates.includes('self') && isCurrentUserWorklog
            
            return isUserSelected || isSelfSelected
        })
    }
    
    const hasWorklog = (date) => {
        const visibleWorklogs = getVisibleWorklogs()
        return visibleWorklogs.some(worklog => {
            // Use moment for reliable date comparison
            const worklogDate = moment(worklog.date).startOf('day')
            const targetDate = moment(date).startOf('day')
            return worklogDate.isSame(targetDate)
        })
    }

    const navigateMonth = (direction) => {
        const newDate = moment(viewDate).add(direction, 'month').toDate()
        setViewDate(newDate)
        onMonthChange(newDate)
    }

    const handleDateClick = (date) => {
        onDateChange(date)
    }

    const daysInMonth = getDaysInMonth(viewDate)
    const firstDayOfMonth = getFirstDayOfMonth(viewDate)
    const days = []
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
        days.push(<div key={`empty-${i}`} className={styles.emptyDay}></div>)
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
        const date = moment(viewDate).date(day).toDate()
        const isSelected = isSameDay(date, selectedDate)
        const isTodayDate = isToday(date)
        const hasWork = hasWorklog(date)
        
        days.push(
            <button
                key={day}
                className={`${styles.day} ${isSelected ? styles.selected : ''} ${isTodayDate ? styles.today : ''} ${hasWork ? styles.hasWorklog : ''}`}
                onClick={() => handleDateClick(date)}
            >
                {day}
            </button>
        )
    }

    return (
        <div className={styles.miniCalendar}>
            <div className={styles.header}>
                <button 
                    className={styles.navButton}
                    onClick={() => navigateMonth(-1)}
                >
                    ‹
                </button>
                <span className={styles.monthTitle}>{getMonthName(viewDate)}</span>
                <button 
                    className={styles.navButton}
                    onClick={() => navigateMonth(1)}
                >
                    ›
                </button>
            </div>
            <div className={styles.weekdays}>
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
                    <div key={index} className={styles.weekday}>{day}</div>
                ))}
            </div>
            <div className={styles.daysGrid}>
                {days}
            </div>
        </div>
    )
}